/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from '@chakra-ui/react';
import FormBase from '@/components/forms/new/FormBase';
import ServiceProfileVisibleScope from './formFields/ServiceProfileVisibleScope';
import DirectRoutingDialPlansField from './formFields/DirectRoutingDialPlansField';
import DirectRoutingRegionsField from './formFields/DirectRoutingRegionsField';
import DirectRoutingVoicePoliciesField from './formFields/DirectRoutingVoicePoliciesField';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import { multiSelectDataMapper, toSentenceCase } from 'utils/utils';
import getServiceProfiles from 'API/getServiceProfiles';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import getDRServiceProfileRegions from 'API/ServiceProfiles/getDRServiceProfileRegions';
import getDRServiceProfileDialPlans from 'API/ServiceProfiles/getDRServiceProfileDialPlans';
import getDRServiceProfileVoicePolicies from 'API/ServiceProfiles/getDRServiceProfileVoicePolicies';
import useFieldValidation from 'hooks/useFieldValidation';

import { validationSchemaDR } from './schema';

import BreadcrumbTrail from 'components/BreadcrumbTrail';

/*
 * Direct Routing Component for Edit/Add
 */
const DirectRouting = ({
    mode,
    data,
    level,
    handleBackButton,
    visibleScopeOptions,
}) => {
    const [voicePolicyEnum, setVoicePolicyEnum] = useState([]);
    const [regionsEnum, setRegionsEnum] = useState([]);
    const [dialPlansEnum, setDialPlansEnum] = useState([]);
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        currentWholesalerData,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    const { darkMode } = useSelector((state) => state.settings);

    const DEFAULT_DR_PAYLOAD = {
        name: 'Direct Routing Wholesaler Profile',
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        voicePolicies: [],
        dialPlans: [],
        regions: [],
        visibleScope: level === 'partner' ? 20 : 60,
    };

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: `${currentWholesalerData?.name} - Direct Routing Wholesaler Profile`,
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - Direct Routing Partner Profile`,
            });
        }
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        partnerID,
        wholesalerID,
        data?.wholesalerID,
        data?.partnerID,
    ]);

    const [modalState, setModalState] = useState(data || DEFAULT_DR_PAYLOAD);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useFieldValidation(modalState, validationSchemaDR);

    /*
     * A function to get the current scope and append it for API calls
     */
    const scope = () => {
        if (level === 'partner') {
            return `Partner/${data?.partnerID || partnerID}`;
        } else {
            return `Wholesaler/${data?.wholesalerID || wholesalerID}`;
        }
    };
    // function to set modal state with a given payload
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    const handleGetVoicePolicies = () => {
        console.log('[DirectRouting] handleGetVoicePolicies called');
        console.log('[DirectRouting] scope:', scope());
        console.log('[DirectRouting] regions:', modalState.regions);
        getDRServiceProfileVoicePolicies({
            scope: scope(),
            regions: modalState.regions,
        })
            .then((res) => {
                console.log('[DirectRouting] VoicePolicies API response:', res);
                setVoicePolicyEnum(res.data.map(multiSelectDataMapper));
            })
            .catch((e) => {
                console.error('[DirectRouting] VoicePolicies API error:', e);
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'An error occurred loading voice policies.',
                );
            });
    };

    const handleGetDropdown = () => {
        setIsLoading(true);
        const fetchSPData = mode === 'EDIT' && data;
        Promise.all([
            fetchSPData
                ? getServiceProfiles({ level, id: data?.id })
                : setTimeout(() => {
                      Promise.resolve();
                  }, 1),
            getDRServiceProfileRegions({ scope: scope() }),
            getDRServiceProfileDialPlans({ scope: scope() }),
        ])
            .then(([spResult, regionsResult, dialPlansResult]) => {
                setRegionsEnum(regionsResult.data.map(multiSelectDataMapper));
                setDialPlansEnum(
                    dialPlansResult.data.map(multiSelectDataMapper),
                );
                if (fetchSPData) {
                    const regions = spResult.data.regions.map(
                        multiSelectDataMapper,
                    );
                    const dialPlans = spResult.data.dialPlans.map(
                        multiSelectDataMapper,
                    );
                    const voicePolicies = spResult.data.voicePolicies.map(
                        multiSelectDataMapper,
                    );

                    setModalState({
                        ...spResult.data,
                        regions,
                        dialPlans,
                        voicePolicies,
                    });
                }
            })
            .catch((e) => {
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'An error occurred loading service profile data.',
                );
            })
            .finally(() => {
                setTimeout(() => {
                    setIsLoading(false);
                }, 500);
            });
    };

    useEffect(() => {
        console.log(
            '[DirectRouting] useEffect regions changed:',
            modalState.regions,
        );
        if (modalState.regions && modalState.regions.length > 0) {
            console.log(
                '[DirectRouting] Calling handleGetVoicePolicies with regions:',
                modalState.regions,
            );
            handleGetVoicePolicies();
        } else {
            console.log(
                '[DirectRouting] Not calling handleGetVoicePolicies: regions empty or undefined',
            );
        }
    }, [modalState.regions]);

    useEffect(() => {
        handleGetDropdown();
    }, [mode, data, level]);

    if (isLoading) {
        return <LoadingIndicator />;
    }

    // Helper to map value(s) to option(s) for select fields
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) => options.find((o) => o.value === (v.value ?? v)))
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find((o) => o.value === (values.value ?? values)) || null
        );
    };
    const initialValues = {
        name: modalState?.name || '',
        regions: mapToOptions(modalState?.regions, regionsEnum),
        dialPlans: mapToOptions(modalState?.dialPlans, dialPlansEnum),
        voicePolicies: mapToOptions(modalState?.voicePolicies, voicePolicyEnum),
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    const finalFormSubmit = async (values, actions) => {
        setSubmitted(true);
        setError(null);
        setIsLoading(true);
        try {
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: 'DirectRouting',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            } else {
                await createServiceProfile({
                    type: 'DirectRouting',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            }

            handleBackButton();
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <ServiceProfileVisibleScope
                            key="directRoutingServiceProfileVisibleScope"
                            name="visibleScope"
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: e.value,
                                }));
                            }}
                        />,
                        <DirectRoutingDialPlansField
                            key="dialPlans"
                            value={modalState.dialPlans}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    dialPlans: val,
                                }))
                            }
                            options={dialPlansEnum}
                            darkMode={darkMode}
                            error={
                                !modalState.dialPlans ||
                                modalState.dialPlans.length === 0
                                    ? 'At least one dial plan is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                            errors={errors}
                            touched={touched}
                        />,
                        <DirectRoutingRegionsField
                            key="regions"
                            value={modalState.regions}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    regions: val,
                                }))
                            }
                            options={regionsEnum}
                            darkMode={darkMode}
                            error={
                                !modalState.regions ||
                                modalState.regions.length === 0
                                    ? 'At least one region is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                            errors={errors}
                            touched={touched}
                        />,
                        <DirectRoutingVoicePoliciesField
                            key="voicePolicies"
                            value={modalState.voicePolicies}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    voicePolicies: val,
                                }))
                            }
                            options={voicePolicyEnum}
                            darkMode={darkMode}
                            error={
                                !modalState.voicePolicies ||
                                modalState.voicePolicies.length === 0
                                    ? 'At least one voice policy is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                            errors={errors}
                            touched={touched}
                        />,
                        error && (
                            <Box
                                paddingBottom="0.5rem"
                                key="error-box-direct-routing">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default DirectRouting;
